import React, { useEffect, useState } from 'react';
import CustomLoading from '../components/customLoading/CustomLoading';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/FormatDateTime';
import { getIotDeviceDetails } from '../services/iotDevices';
import {
    Wifi, WifiOff, Zap, ZapOff, Signal, Phone, Clock,
    CreditCard, CheckCircle2, XCircle, AlertTriangle, RefreshCw,
    Activity, Timer, User, Settings2
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function Badge({ active, label }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            padding: '3px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 600,
            background: active ? '#dcfce7' : '#fee2e2',
            color: active ? '#16a34a' : '#dc2626',
        }}>
            {active ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
            {label}
        </span>
    );
}

function SectionTitle({ icon: Icon, label, color = '#3b6cf8' }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '20px 0 10px', borderBottom: '1px solid #f0f0f0', paddingBottom: '8px' }}>
            <Icon size={15} color={color} />
            <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#555' }}>{label}</span>
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f7f7f7' }}>
            <span style={{ fontSize: '12px', color: '#888', fontWeight: 500 }}>{label}</span>
            <span style={{ fontSize: '13px', color: '#222', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value ?? '—'}</span>
        </div>
    );
}

function LogEntry({ state, timestamp, stateLabels }) {
    const isOn = state === '1';
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '6px 10px', borderRadius: '6px', marginBottom: '4px',
            background: isOn ? '#f0fdf4' : '#fafafa',
            border: `1px solid ${isOn ? '#bbf7d0' : '#e5e7eb'}`,
        }}>
            <span style={{
                width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                background: isOn ? '#16a34a' : '#d1d5db',
            }} />
            <span style={{ fontSize: '12px', fontWeight: 600, color: isOn ? '#16a34a' : '#6b7280', minWidth: '48px' }}>
                {isOn ? stateLabels[1] : stateLabels[0]}
            </span>
            <span style={{ fontSize: '11px', color: '#9ca3af', marginLeft: 'auto' }}>
                {new Date(timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            </span>
        </div>
    );
}

function UptimeRow({ date, motorUptime, elecUptime }) {
    const fmt = (val) => {
        if (!val || val === '00:00:00') return <span style={{ color: '#ccc' }}>—</span>;
        const [h, m] = val.split(':');
        const hrs = parseInt(h);
        const mins = parseInt(m);
        if (hrs === 0 && mins === 0) return <span style={{ color: '#ccc' }}>—</span>;
        return <span style={{ color: '#3b6cf8', fontWeight: 600 }}>{hrs > 0 ? `${hrs}h ` : ''}{mins}m</span>;
    };
    return (
        <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={{ padding: '7px 8px', fontSize: '12px', color: '#555' }}>{date}</td>
            <td style={{ padding: '7px 8px', fontSize: '12px', textAlign: 'center' }}>{fmt(motorUptime)}</td>
            <td style={{ padding: '7px 8px', fontSize: '12px', textAlign: 'center' }}>{fmt(elecUptime)}</td>
        </tr>
    );
}

function PaymentRow({ payment }) {
    const statusColor = payment.payment_status === 'COMPLETED' ? '#16a34a' : '#f59e0b';
    return (
        <div style={{
            padding: '10px 12px', borderRadius: '8px', marginBottom: '6px',
            border: '1px solid #f0f0f0', background: '#fafafa',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
        }}>
            <div>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: '#222' }}>₹{payment.amount}</p>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#aaa' }}>
                    {new Date(payment.payment_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
            </div>
            <span style={{
                padding: '3px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 600,
                background: payment.payment_status === 'COMPLETED' ? '#dcfce7' : '#fef9c3',
                color: statusColor,
            }}>
                {payment.payment_status}
            </span>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function IotDeviceDetailsPop({ item }) {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    const [activeLogTab, setActiveLogTab] = useState('motor');
    
    // Countdown Timer State
    const [cdHours, setCdHours] = useState('00');
    const [cdMins, setCdMins] = useState('00');

    useEffect(() => {
        if (!item?.device_id) return;
        const fetchDeviceDetails = async () => {
            setLoading(true);
            try {
                const res = await getIotDeviceDetails(item.device_id);
                if (res.status === 200) {
                    setDetails(res.data);
                    // Initialize countdown timer state
                    if (res.data.timer && res.data.timer.length === 4) {
                        setCdHours(res.data.timer.substring(0, 2));
                        setCdMins(res.data.timer.substring(2, 4));
                    }
                } else {
                    toast.error('Failed to load device details');
                }
            } catch (err) {
                console.error(err);
                toast.error('Error loading device details');
            } finally {
                setLoading(false);
            }
        };
        fetchDeviceDetails();
    }, [item?.device_id]);

    // Update Device State (Motor, Auto, Dry Run)
    const handleToggleState = async (key, currentVal) => {
        const newVal = currentVal === "1" ? "0" : "1";
        const loadingToast = toast.loading(`Updating ${key}...`);
        try {
            const res = await fetch("https://extended.rashailagro.in/gsm-iot/senddevicedata/", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ device_id: details.device_id, [key]: Number(newVal) })
            });
            if (res.ok) {
                toast.update(loadingToast, { render: `${key.toUpperCase()} updated successfully`, type: "success", isLoading: false, autoClose: 2000 });
                setDetails(prev => ({ ...prev, [key]: newVal }));
            } else {
                toast.update(loadingToast, { render: `Failed to update ${key}`, type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            toast.update(loadingToast, { render: "Network error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    // Update Countdown Timer (on Device Level via the provided URL pattern)
    const handleUpdateCountdown = async () => {
        const h = cdHours.toString().padStart(2, '0');
        const m = cdMins.toString().padStart(2, '0');
        const timerVal = `${h}${m}`;
        const loadingToast = toast.loading("Updating countdown timer...");
        try {
            const res = await fetch(`https://extended.rashailagro.in/gsm-iot/gsm-scheduler/${details.id}/`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ timer: timerVal })
            });
            if (res.ok) {
                toast.update(loadingToast, { render: "Timer updated successfully", type: "success", isLoading: false, autoClose: 2000 });
                setDetails(prev => ({ ...prev, timer: timerVal }));
            } else {
                toast.update(loadingToast, { render: "Failed to update timer", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            toast.update(loadingToast, { render: "Network error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    // Update Scheduled Timers (Active State & Start/End times)
    const handleUpdateScheduler = async (schedId, payload) => {
        const loadingToast = toast.loading("Updating scheduler...");
        try {
            const res = await fetch(`https://extended.rashailagro.in/gsm-iot/gsm-scheduler/${schedId}/`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                toast.update(loadingToast, { render: "Scheduler updated", type: "success", isLoading: false, autoClose: 2000 });
                setDetails(prev => ({
                    ...prev,
                    scheduled_timers: prev.scheduled_timers.map(st => 
                        st.id === schedId ? { ...st, ...payload } : st
                    )
                }));
            } else {
                toast.update(loadingToast, { render: "Failed to update scheduler", type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (e) {
            toast.update(loadingToast, { render: "Network error", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    if (loading) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CustomLoading size={60} />
        </div>
    );

    if (!details) return null;

    const subExpired = details.subscription_end_date && new Date(details.subscription_end_date) < new Date();

    const tabs = [
        { key: 'info', label: 'Info' },
        { key: 'timers', label: 'Timers' },
        { key: 'logs', label: 'Logs' },
        { key: 'uptime', label: 'Uptime' },
        { key: 'payments', label: 'Payments' },
    ];

    return (
        <div style={{ padding: '4px 2px', fontFamily: 'system-ui, sans-serif' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                    <p style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#111', fontFamily: 'monospace' }}>{details.device_id}</p>
                    <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#777' }}>{details.device_name}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    <Badge active={details.is_on} label={details.is_on ? 'Online' : 'Offline'} />
                    <Badge active={!subExpired} label={subExpired ? 'Expired' : 'Active'} />
                </div>
            </div>

            {/* Live Status Pills */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                {[
                    { icon: details.motor === '1' ? Zap : ZapOff, label: details.motor === '1' ? 'Motor ON' : 'Motor OFF', color: details.motor === '1' ? '#f59e0b' : '#9ca3af' },
                    { icon: details.electricity === '1' ? Activity : AlertTriangle, label: details.electricity === '1' ? 'Power OK' : 'No Power', color: details.electricity === '1' ? '#16a34a' : '#ef4444' },
                    { icon: details.auto === '1' ? RefreshCw : Timer, label: details.auto === '1' ? 'Auto ON' : 'Manual', color: details.auto === '1' ? '#8b5cf6' : '#9ca3af' },
                    { icon: Signal, label: `Signal: ${details.signal}`, color: '#3b6cf8' },
                ].map(({ icon: Icon, label, color }) => (
                    <span key={label} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '4px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 600,
                        background: `${color}15`, color: color, border: `1px solid ${color}33`
                    }}>
                        <Icon size={11} /> {label}
                    </span>
                ))}
            </div>

            {/* Quick Controls Section */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', padding: '10px', background: '#f8f9fc', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <button
                    onClick={() => handleToggleState('motor', details.motor)}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: details.motor === '1' ? '#fee2e2' : '#dcfce7', color: details.motor === '1' ? '#dc2626' : '#16a34a', cursor: 'pointer', fontWeight: 600, fontSize: '12px', transition: 'all 0.2s' }}
                >
                    {details.motor === '1' ? 'Turn Motor OFF' : 'Turn Motor ON'}
                </button>
                <button
                    onClick={() => handleToggleState('auto', details.auto)}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: details.auto === '1' ? '#f3e8ff' : '#fff', color: details.auto === '1' ? '#7e22ce' : '#374151', cursor: 'pointer', fontWeight: 600, fontSize: '12px', transition: 'all 0.2s' }}
                >
                    Auto {details.auto === '1' ? 'ON' : 'OFF'}
                </button>
                <button
                    onClick={() => handleToggleState('dry_run', details.dry_run)}
                    style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: details.dry_run === '1' ? '#fef3c7' : '#fff', color: details.dry_run === '1' ? '#d97706' : '#374151', cursor: 'pointer', fontWeight: 600, fontSize: '12px', transition: 'all 0.2s' }}
                >
                    Dry Run {details.dry_run === '1' ? 'ON' : 'OFF'}
                </button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '2px solid #f0f0f0', marginBottom: '4px', gap: '4px' }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
                        padding: '7px 14px', fontSize: '12px', fontWeight: 600, border: 'none',
                        background: 'none', cursor: 'pointer', borderBottom: activeTab === t.key ? '2px solid #3b6cf8' : '2px solid transparent',
                        color: activeTab === t.key ? '#3b6cf8' : '#888', marginBottom: '-2px', transition: 'color 0.15s',
                    }}>
                        {t.label}
                        {t.key === 'payments' && details.payment_history?.length > 0 && (
                            <span style={{ marginLeft: '5px', background: '#3b6cf8', color: '#fff', borderRadius: '99px', padding: '1px 6px', fontSize: '10px' }}>
                                {details.payment_history.length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {/* Tab: Info */}
            {activeTab === 'info' && (
                <div>
                    <SectionTitle icon={User} label="Owner" />
                    <InfoRow label="Owner Number" value={details.owner_number} />
                    {details.guest_user1_number && <InfoRow label="Guest 1" value={details.guest_user1_number} />}
                    {details.guest_user2_number && <InfoRow label="Guest 2" value={details.guest_user2_number} />}
                    {details.guest_user3_number && <InfoRow label="Guest 3" value={details.guest_user3_number} />}
                    {details.guest_user4_number && <InfoRow label="Guest 4" value={details.guest_user4_number} />}

                    <SectionTitle icon={CreditCard} label="Subscription" color="#8b5cf6" />
                    <InfoRow label="Start Date" value={details.subscription_start_date ? formatDate(details.subscription_start_date) : null} />
                    <InfoRow label="End Date" value={
                        <span style={{ color: subExpired ? '#dc2626' : '#222' }}>
                            {details.subscription_end_date ? formatDate(details.subscription_end_date) : '—'}
                            {subExpired && ' (Expired)'}
                        </span>
                    } />
                    <InfoRow label="Is Active" value={<Badge active={details.is_active} label={details.is_active ? 'Yes' : 'No'} />} />

                    <SectionTitle icon={Settings2} label="Device Config" color="#f59e0b" />
                    <InfoRow label="Phase" value={details.phase} />
                    <InfoRow label="Current" value={details.current} />
                    <InfoRow label="Current Upper" value={details.current_upper_val} />
                    <InfoRow label="Current Lower" value={details.current_lower_val} />
                    <InfoRow label="Notifications" value={<Badge active={details.is_notification} label={details.is_notification ? 'Enabled' : 'Disabled'} />} />
                    <InfoRow label="Registered" value={<Badge active={details.is_registered} label={details.is_registered ? 'Yes' : 'No'} />} />
                    <InfoRow label="Auto Managed" value={<Badge active={details.auto_managed} label={details.auto_managed ? 'Yes' : 'No'} />} />
                </div>
            )}

            {/* Tab: Timers (NEW) */}
            {activeTab === 'timers' && (
                <div>
                    <SectionTitle icon={Timer} label="Countdown Timer" color="#f59e0b" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fafafa', padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#777', fontWeight: 600 }}>Hours</span>
                            <input
                                type="number" min="0" max="99"
                                value={cdHours} onChange={e => setCdHours(e.target.value)}
                                style={{ width: '56px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', textAlign: 'center', fontSize: '14px' }}
                            />
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: 700, marginTop: '14px', color: '#555' }}>:</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontSize: '11px', color: '#777', fontWeight: 600 }}>Mins</span>
                            <input
                                type="number" min="0" max="59"
                                value={cdMins} onChange={e => setCdMins(e.target.value)}
                                style={{ width: '56px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', textAlign: 'center', fontSize: '14px' }}
                            />
                        </div>
                        <button
                            onClick={handleUpdateCountdown}
                            style={{ marginLeft: 'auto', marginTop: '14px', padding: '8px 16px', background: '#3b6cf8', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '12px', transition: 'background 0.2s' }}
                        >
                            Set Timer
                        </button>
                    </div>

                    <SectionTitle icon={Clock} label="Scheduled Timers" color="#3b6cf8" />
                    {details.scheduled_timers?.length > 0 ? (
                        details.scheduled_timers.map((st, i) => (
                            <div key={st.id} style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: '#fafafa', padding: '14px', borderRadius: '8px', border: `1px solid ${st.is_active ? '#bbf7d0' : '#f0f0f0'}`, marginBottom: '10px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 700, color: '#444' }}>Schedule {i + 1}</span>
                                    <button
                                        onClick={() => handleUpdateScheduler(st.id, { is_active: !st.is_active })}
                                        style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 600, border: 'none', borderRadius: '99px', cursor: 'pointer', background: st.is_active ? '#dcfce7' : '#fee2e2', color: st.is_active ? '#16a34a' : '#dc2626' }}
                                    >
                                        {st.is_active ? 'Active (Click to Disable)' : 'Inactive (Click to Enable)'}
                                    </button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <span style={{ fontSize: '10px', color: '#888', marginBottom: '2px', textTransform: 'uppercase' }}>Start Time</span>
                                        <input
                                            type="time" step="1"
                                            defaultValue={st.start_time}
                                            onBlur={(e) => { if (e.target.value !== st.start_time) handleUpdateScheduler(st.id, { start_time: e.target.value }) }}
                                            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <span style={{ fontSize: '10px', color: '#888', marginBottom: '2px', textTransform: 'uppercase' }}>End Time</span>
                                        <input
                                            type="time" step="1"
                                            defaultValue={st.end_time}
                                            onBlur={(e) => { if (e.target.value !== st.end_time) handleUpdateScheduler(st.id, { end_time: e.target.value }) }}
                                            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '100%' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '12px 0' }}>No scheduled timers configured</p>
                    )}
                </div>
            )}

            {/* Tab: Logs */}
            {activeTab === 'logs' && (
                <div>
                    <div style={{ display: 'flex', gap: '6px', margin: '10px 0 14px' }}>
                        {[
                            { key: 'motor', label: 'Motor Logs', icon: Zap, color: '#f59e0b', count: details.motor_logs?.length },
                            { key: 'elec', label: 'Electricity Logs', icon: Activity, color: '#3b6cf8', count: details.elec_logs?.length },
                        ].map(({ key, label, icon: Icon, color, count }) => (
                            <button
                                key={key}
                                onClick={() => setActiveLogTab(key)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    padding: '6px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
                                    border: `1.5px solid ${activeLogTab === key ? color : '#e5e7eb'}`,
                                    background: activeLogTab === key ? `${color}12` : '#fff',
                                    color: activeLogTab === key ? color : '#888',
                                    cursor: 'pointer', transition: 'all 0.15s',
                                }}
                            >
                                <Icon size={12} />
                                {label}
                                {count > 0 && (
                                    <span style={{
                                        background: activeLogTab === key ? color : '#e5e7eb',
                                        color: activeLogTab === key ? '#fff' : '#888',
                                        borderRadius: '99px', padding: '1px 6px', fontSize: '10px',
                                    }}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    {activeLogTab === 'motor' && (
                        details.motor_logs?.length > 0
                            ? details.motor_logs.map((log, i) => (
                                <LogEntry key={i} state={log.state} timestamp={log.timestamp} stateLabels={['OFF', 'ON']} />
                            ))
                            : <p style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '20px 0' }}>No motor logs</p>
                    )}
                    {activeLogTab === 'elec' && (
                        details.elec_logs?.length > 0
                            ? details.elec_logs.map((log, i) => (
                                <LogEntry key={i} state={log.state} timestamp={log.timestamp} stateLabels={['Cut', 'Present']} />
                            ))
                            : <p style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '20px 0' }}>No electricity logs</p>
                    )}
                </div>
            )}

            {/* Tab: Uptime */}
            {activeTab === 'uptime' && (
                <div>
                    <SectionTitle icon={Clock} label="Daily Uptime History" color="#16a34a" />
                    {details.uptime_history?.length > 0 ? (
                        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fc' }}>
                                        <th style={{ padding: '8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', textAlign: 'left' }}>Date</th>
                                        <th style={{ padding: '8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', textAlign: 'center' }}>Motor</th>
                                        <th style={{ padding: '8px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#888', textAlign: 'center' }}>Electricity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.uptime_history.map((row, i) => (
                                        <UptimeRow key={i} date={row.date} motorUptime={row.motor_uptime} elecUptime={row.electricity_uptime} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '12px 0' }}>No uptime history</p>
                    )}
                </div>
            )}

            {/* Tab: Payments */}
            {activeTab === 'payments' && (
                <div>
                    <SectionTitle icon={CreditCard} label="Payment History" color="#8b5cf6" />
                    {details.payment_history?.length > 0
                        ? details.payment_history.map((p) => <PaymentRow key={p.id} payment={p} />)
                        : <p style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '12px 0' }}>No payment records</p>
                    }
                </div>
            )}
        </div>
    );
}

export default IotDeviceDetailsPop;